import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Radio, Clock, RefreshCw } from 'lucide-react';
import { useCompetition, useLiveScores } from '@/hooks/useApi';

const STATUS_COLORS: Record<string, string> = {
  notStarted: 'bg-gray-200 text-gray-700',
  live: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  upcoming: 'bg-yellow-100 text-yellow-700',
};

export default function CompetitionScorePage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const { t, i18n } = useTranslation();
  const { data: competition } = useCompetition(competitionId || '');
  const { data: scores, isLoading } = useLiveScores(competitionId);

  const name = competition
    ? (i18n.language === 'en' ? competition.nameEn : competition.name)
    : '';

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Link
        to="/scores"
        className="inline-flex items-center gap-1 text-primary text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <ArrowLeft className="w-4 h-4" /> {t('common.backToList')}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">{name || t('scores.title')}</h1>
        <span className="flex items-center gap-1 text-red-500 text-sm">
          <Radio className="w-4 h-4 animate-pulse" /> {t('scores.autoRefresh')}
        </span>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold">{t('scores.result')}</h2>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Clock className="w-3 h-3" />
            <span>{t('common.lastUpdated')}: {new Date().toLocaleTimeString()}</span>
            <RefreshCw className="w-3 h-3 animate-spin" />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-bg-secondary rounded" />
            ))}
          </div>
        ) : Array.isArray(scores) && scores.length > 0 ? (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-primary/20">
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.rank')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.athlete')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.township')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.result')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('common.filter')}</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((score) => (
                <tr key={score.id} className="border-b border-border/50 hover:bg-[var(--card-hover)]">
                  <td className="py-3 px-2 font-bold text-lg">
                    {score.rank <= 3 ? (
                      <span className={score.rank === 1 ? 'text-yellow-500' : score.rank === 2 ? 'text-gray-400' : 'text-amber-700'}>
                        {score.rank}
                      </span>
                    ) : score.rank}
                  </td>
                  <td className="py-3 px-2 font-medium">{score.athleteName}</td>
                  <td className="py-3 px-2 text-text-secondary">{score.township}</td>
                  <td className="py-3 px-2 font-mono font-medium">{score.result}</td>
                  <td className="py-3 px-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[score.status]}`}>
                      {t(`scores.status.${score.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12 text-text-secondary">{t('scores.noResults')}</div>
        )}
      </div>
    </div>
  );
}
